import { inject, injectable } from "inversify";
import React from "react";
import { Constants } from "../../constants.generated";
import { EventConstants } from "../../EventConstants";
import { TYPES } from "../../ioc/types";
import { SelectedTier } from "../../models/app";
import type { TierSelectorService, UrlService, UserTierService } from "../../services";
import { ServiceBase } from "../../services/ServiceBase";
import { SelectedTierComponent } from "../components/SelectedTierComponent";
import { SelectTierButton } from "../components/SelectTierButton";
import { ReactDOMProxy } from "../ReactDOMProxy";

/**
 * MUI tier selector service. Used to render and manage the tier selector for the application.
 * Covers both the global tier selector and input level tier selectors.
 */
@injectable()
export class MaterialTierSelectorService extends ServiceBase implements TierSelectorService {

	private globalTierSelector: JSX.Element | undefined;

	/**
	 * Initialises a new instance of the MaterialDataGridService class.
	 * @param userTierService User tier service, used to check the users current tier settings and get the current page level tier.
	 * @param urlService URL service, used to generate URLs.
	 */
	public constructor(
		@inject(TYPES.UserTierService) private userTierService: UserTierService,
		@inject(TYPES.UrlService) private urlService: UrlService) {
		super();
	}

	/**
	 * @inheritdoc
	 */
	public applyGlobalTierSelectors(target: Element) {

		target.querySelectorAll("div[data-tier-selector]").forEach(element => this.renderGlobalTierSelector(element));
	}

	/**
	 * @inheritdoc
	 */
	public applyInputTierSelectors(target: Element) {

		target.querySelectorAll("input[data-tier-selector]").forEach((element) => this.renderInputTierSelector(element));
	}

	/**
	 * @inheritdoc
	 */
	public setTierSelectorValue(value: any, valueField: HTMLInputElement) {

		const selectedTier = value as SelectedTier;

		if (selectedTier)
			valueField.value = JSON.stringify(selectedTier);
	}

	/**
	 * Renders the global tier selector.
	 * @param element The target element for the global tier selector.
	 */
	private renderGlobalTierSelector(element: Element) {

		if (!this.globalTierSelector) {
			const requireBrokerSelection = element.hasAttribute("data-require-broker-selection");
			const requireGroupSelection = element.hasAttribute("data-require-group-selection");
			const urlParams = this.urlService.getQueryParams();
			const brokerId = this.userTierService.getPageTier()?.broker?.id;
			const groupId = this.userTierService.getPageTier()?.group?.id;
			const autoOpen = (requireBrokerSelection && !(brokerId || urlParams.get("tier3"))) || (requireGroupSelection && !(groupId || urlParams.get("tier1")));
			const onCancel = requireBrokerSelection || requireGroupSelection ? () => {

				// check if tier should be selected or not, if it's required and none selected, send the user to loan status
				if ((requireBrokerSelection && !brokerId) || (requireGroupSelection && !groupId)) {
					window.location.href = this.urlService.url('~/');
					return false;
				}
				return true;
			} : undefined;

			this.globalTierSelector = <SelectedTierComponent autoOpen={autoOpen} requiresBroker={requireBrokerSelection} requiresGroup={requireGroupSelection} onDialogCancel={onCancel} />;

			// output global tier selector component
			ReactDOMProxy.renderStyled(
				this.globalTierSelector,
				element
			);
		}
	}

	/**
	 * Renders an input level tier selector. Allowing tier selection integrated with an input element.
	 * @param element The input element.
	 */
	private renderInputTierSelector(element: Element) {

		if (!element.parentElement) {
			return;
		}

		const input = element as HTMLInputElement;
		const inputGroup = input.parentElement;

		if (!inputGroup?.classList.contains("input-group")) {
			return;
		}

		// get or create an input group append element, to place the select tier button into
		let inputGroupAppend = inputGroup.querySelector(".input-group-append");

		if (!inputGroupAppend) {
			inputGroupAppend = document.createElement("div");
			inputGroupAppend.classList.add("input-group-append");
			inputGroup.appendChild(inputGroupAppend);
		}

		const inputText = inputGroup.querySelector("input[type=text]") as HTMLInputElement;

		if (inputText && !inputText.getAttribute("autocomplete"))
			inputText.setAttribute("autocomplete", "off");

		// ensure the root tier for the input control is fixed to the page tier
		let rootTier: SelectedTier = {};
		if (input.getAttribute("data-tier-root") === Constants.TierKeyPageTier) {
			rootTier = this.userTierService.getPageTier() ?? {};

			// subscribe to the page tier change event
			this.userTierService.subscribe(input.id || input.name, (tier: SelectedTier | null) => {
				if (tier) {
					rootTier.group = tier.group;
					rootTier.branch = tier.branch;
					rootTier.broker = tier.broker;
				}
				else {
					rootTier.group = undefined;
					rootTier.branch = undefined;
					rootTier.broker = undefined;
				}
			});
		}

		// get the current tier value for the input control
		let selectedTier: SelectedTier | undefined;
		if (input.value) {
			selectedTier = JSON.parse(input.value) as SelectedTier;
		}
		else if (input.getAttribute("data-tier-default") === Constants.TierKeyPageTier) {
			selectedTier = this.userTierService.getPageTier() ?? undefined;
		}
		else {
			selectedTier = {};
		}

		// ensure default text for the input
		const defaultText = input.getAttribute("data-tier-default-text") ?? "Finance House";

		if (selectedTier && inputText && !inputText.value) {
			inputText.value = MaterialTierSelectorService.getSelectedTierPath(selectedTier, defaultText);
			this.setTierSelectorValue(selectedTier, input);
		}

		// if the user is not allowed to change broker then this control should not be editable at all
		if (!this.userTierService.isAllowedToChangeBroker()) {
			const inputGroupPrepend = inputGroup.querySelector(".input-group-prepend");
			if (inputGroupPrepend) {
				inputGroupPrepend.remove();
			}
			if (inputText) {
				inputText.disabled = true;
			}
			return;
		}

		// tier selected handler
		const tierSelected = (tier: SelectedTier) => {

			selectedTier = tier;

			// set the hidden input value
			this.setTierSelectorValue(tier, input);

			if (inputText) {
				inputText.value = MaterialTierSelectorService.getSelectedTierPath(tier, defaultText);
			}

			input.dispatchEvent(new Event(EventConstants.change));
		};

		// add a change event lister to the text box
		// this keeps the selectedTier value in sync
		input.addEventListener(EventConstants.change, () => {

			const json = input.value;
			const tier: SelectedTier = json ? (JSON.parse(input.value) as SelectedTier) : {};

			if (tier && selectedTier) {

				selectedTier.group = tier.group;
				selectedTier.branch = tier.branch;
				selectedTier.broker = tier.broker;
			}
			else {
				selectedTier = {};
			}
		});

		// get tier button text or default
		const label = element.getAttribute("data-tier-button-label") || "Find";
		// do the same for the tooltip
		const tooltip = element.getAttribute("data-button-tooltip") || "Find the appropriate tier";

		// output select tier button, which includes a button to open the tier selector and the tier selector
		ReactDOMProxy.renderStyled(
			<SelectTierButton label={label} tooltip={tooltip} selectedTier={selectedTier} onSelect={tierSelected} rootTier={rootTier} />,
			inputGroupAppend
		);
	}

	/**
	 * Gets the selected tier path from a tier, or the supplied default text if there is no path.
	 * @param tier The selected tier.
	 * @param defaultText
	 */
	private static getSelectedTierPath(tier: SelectedTier, defaultText: string) {

		let path: string;
		if (tier.broker?.id) {
			path = tier.broker.path;
		}
		else if (tier.branch?.id) {
			path = tier.branch.path;
		}
		else {
			path = tier.group?.path ?? (defaultText);
		}
		return path;
	}
}